import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Alert,
  Snackbar,
} from '@mui/material';
import { motion } from 'framer-motion';
import axios from 'axios';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post('http://localhost:5000/api/contact', formData);
      setSnackbarMessage('Thank you for contacting us! We will get back to you soon.');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      setSnackbarMessage('Failed to send message. Please try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const storeLocations = [
    {
      name: 'Vengamedu, Karur',
      address: 'Vengamedu, Karur District, Tamil Nadu',
      phone: '+91 6382939365',
      email: 'vengamedu@vallivelli.com',
    },
    {
      name: 'Paramathi Velur',
      address: 'Paramathi Velur, Namakkal District, Tamil Nadu',
      phone: '+91 9944683233',
      email: 'paramathi@vallivelli.com',
    },
    {
      name: 'Namakkal',
      address: 'Namakkal, Tamil Nadu',
      phone: '+91 9894058033',
      email: 'namakkal@vallivelli.com',
    },
  ];

  const contactInfo = [
    {
      icon: <PhoneIcon sx={{ fontSize: 40, color: '#D4AF37' }} />,
      title: 'Call Us',
      content: '+91 6382939365',
    },
    {
      icon: <EmailIcon sx={{ fontSize: 40, color: '#D4AF37' }} />,
      title: 'Email Us',
      content: 'info@vallivelli.com',
    },
    {
      icon: <AccessTimeIcon sx={{ fontSize: 40, color: '#D4AF37' }} />,
      title: 'Store Hours',
      content: 'Mon - Sat: 9:00 AM - 8:00 PM\nSunday: 10:00 AM - 6:00 PM',
    },
  ];

  return (
    <Box sx={{ backgroundColor: '#FBF9F7', minHeight: '100vh', py: { xs: 3, md: 6 } }}>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              mb: 1,
              fontWeight: 700,
              color: '#1A1A2E',
              fontSize: { xs: '1.6rem', sm: '2rem', md: '2.5rem' },
            }}
          >
            Get In Touch
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              mb: 6,
              color: '#555555',
            }}
          >
            We'd love to hear from you. Visit our store or reach out to us.
          </Typography>
        </motion.div>

        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              mb: 1,
              fontWeight: 700,
              color: '#1A1A2E',
              fontSize: { xs: '1.3rem', sm: '1.6rem', md: '2rem' },
            }}
          >
            Our Store Locations
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              mb: 4,
              color: '#555555',
            }}
          >
            Visit us at any of our three convenient locations
          </Typography>

          <Grid container spacing={3} sx={{ mb: 6 }}>
            {storeLocations.map((store, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <Paper
                    sx={{
                      p: 3,
                      height: '100%',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      borderRadius: 2,
                      border: '2px solid #1B1464',
                      transition: 'all 0.3s',
                      '&:hover': {
                        boxShadow: '0 8px 30px rgba(27, 20, 100, 0.2)',
                        borderColor: '#D4AF37',
                      },
                    }}
                  >
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <LocationOnIcon sx={{ fontSize: 50, color: '#1B1464' }} />
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                        color: '#1B1464',
                        textAlign: 'center',
                      }}
                    >
                      {store.name}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <LocationOnIcon sx={{ color: '#666', fontSize: 20, mt: 0.3 }} />
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {store.address}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon sx={{ color: '#666', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {store.phone}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon sx={{ color: '#666', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {store.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Grid container spacing={4} sx={{ mb: 6 }}>
          {contactInfo.map((info, index) => (
            <Grid item xs={6} sm={6} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <Paper
                  sx={{
                    p: { xs: 2, md: 3 },
                    textAlign: 'center',
                    height: '100%',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    borderRadius: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <Box sx={{ mb: 2 }}>{info.icon}</Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 1, color: '#1A1A2E' }}
                  >
                    {info.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#666', whiteSpace: 'pre-line' }}
                  >
                    {info.content}
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Paper
                sx={{
                  p: { xs: 2.5, md: 4 },
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, mb: 3, color: '#1A1A2E', fontSize: { xs: '1.3rem', md: '2rem' } }}
                >
                  Send Us a Message
                </Typography>
                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Your Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    sx={{ mb: 3 }}
                  />
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    sx={{ mb: 3 }}
                  />
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    sx={{ mb: 3 }}
                  />
                  <TextField
                    fullWidth
                    label="Your Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    multiline
                    rows={4}
                    sx={{ mb: 3 }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{
                      backgroundColor: '#1B1464',
                      color: 'white',
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: '#0F0D3D',
                      },
                    }}
                  >
                    Send Message
                  </Button>
                </form>
              </Paper>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Paper
                sx={{
                  p: { xs: 2.5, md: 4 },
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  borderRadius: 2,
                  height: '100%',
                  background: 'linear-gradient(135deg, #1B1464 0%, #2D1B69 100%)',
                  color: 'white',
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, fontSize: { xs: '1.2rem', md: '2rem' } }}>
                  Why Choose VALLI VELLI MALIGAI?
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    ✨ Premium Quality
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    We offer only the finest silver jewelry with certified purity.
                  </Typography>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    🎨 Exquisite Designs
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Traditional and contemporary designs crafted by skilled artisans.
                  </Typography>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    💎 Trusted Brand
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Serving customers with trust and transparency for years.
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    🏆 Customer Satisfaction
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Your happiness is our priority. We ensure the best shopping experience.
                  </Typography>
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>

        <Box sx={{ mt: 6 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Paper
              sx={{
                p: 0,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                borderRadius: 2,
                overflow: 'hidden',
                height: { xs: 250, md: 400 },
              }}
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3886.8267165144835!2d80.2707!3d13.0827!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTPCsDA0JzU3LjciTiA4MMKwMTYnMTQuNSJF!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Store Location"
              />
            </Paper>
          </motion.div>
        </Box>
      </Container>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;
